/**
 * Finance Page
 * Displays financial summary by period with CSV export
 * Requirements: 16.6, 16.7
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Wallet,
  Download,
  TrendingUp,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Loader2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  CheckCircle,
  Package,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  useFinanceSummary,
  useExportFinanceCSV,
  parseAmount,
  getPeriodLabel,
} from '@/hooks/useFinance';
import { useToast } from '@/components/ui/toaster';
import type { FinancePeriod } from '@/lib/api';

/**
 * Format date for API (YYYY-MM-DD)
 */
function formatDateForApi(date: Date): string {
  return date.toISOString().split('T')[0] || '';
}

/**
 * Navigate date by period
 */
function navigateDate(date: Date, period: FinancePeriod, direction: 'prev' | 'next'): Date {
  const newDate = new Date(date);
  const delta = direction === 'next' ? 1 : -1;

  switch (period) {
    case 'day':
      newDate.setDate(newDate.getDate() + delta);
      break;
    case 'week':
      newDate.setDate(newDate.getDate() + delta * 7);
      break;
    case 'month':
      newDate.setMonth(newDate.getMonth() + delta);
      break;
  }

  return newDate;
}

/**
 * Format period range for display
 */
function formatPeriodRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start.toDateString() === end.toDateString()) {
    return formatDate(start);
  }

  return `${formatDate(start)} — ${formatDate(end)}`;
}

export function FinancePage() {
  const [period, setPeriod] = useState<FinancePeriod>('day');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');

  const { toast } = useToast();

  // Fetch financial summary
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useFinanceSummary({
    period,
    date: formatDateForApi(selectedDate),
  });

  // Export mutation
  const exportMutation = useExportFinanceCSV();

  // Parsed amounts
  const amounts = useMemo(() => {
    if (!data?.summary) {
      return {
        totalRevenue: 0,
        totalCommission: 0,
        totalCommissionDebt: 0,
        cardAmount: 0,
        cardCount: 0,
        cashAmount: 0,
        cashCount: 0,
        completedAmount: 0,
        completedCount: 0,
      };
    }

    const { summary } = data;
    return {
      totalRevenue: parseAmount(summary.totalRevenue),
      totalCommission: parseAmount(summary.totalCommission),
      totalCommissionDebt: parseAmount(summary.totalCommissionDebt),
      cardAmount: parseAmount(summary.ordersByPaymentMethod.card.amount),
      cardCount: summary.ordersByPaymentMethod.card.count,
      cashAmount: parseAmount(summary.ordersByPaymentMethod.cash.amount),
      cashCount: summary.ordersByPaymentMethod.cash.count,
      completedAmount: parseAmount(summary.completedOrders.amount),
      completedCount: summary.completedOrders.count,
    };
  }, [data]);

  // Handlers
  const handlePrevPeriod = () => {
    setSelectedDate(navigateDate(selectedDate, period, 'prev'));
  };

  const handleNextPeriod = () => {
    const nextDate = navigateDate(selectedDate, period, 'next');
    // Don't allow future dates
    if (nextDate <= new Date()) {
      setSelectedDate(nextDate);
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleOpenExportDialog = () => {
    // Default to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setExportDateFrom(formatDateForApi(firstDay));
    setExportDateTo(formatDateForApi(lastDay));
    setExportDialogOpen(true);
  };

  const handleExport = async () => {
    if (!exportDateFrom || !exportDateTo) {
      toast({
        title: 'Ошибка',
        description: 'Укажите даты для экспорта',
        variant: 'destructive',
      });
      return;
    }

    try {
      await exportMutation.mutateAsync({
        dateFrom: exportDateFrom,
        dateTo: exportDateTo,
      });
      toast({
        title: 'Экспорт завершён',
        description: 'CSV файл загружен',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      setExportDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Ошибка экспорта',
        description: err instanceof Error ? err.message : 'Не удалось экспортировать данные',
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const isNextDisabled = navigateDate(selectedDate, period, 'next') > new Date();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Финансы</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenExportDialog}
          >
            <Download className="mr-2 h-4 w-4" />
            Экспорт CSV
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2">
            {/* Period Type */}
            <Select value={period} onValueChange={(v) => setPeriod(v as FinancePeriod)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">День</SelectItem>
                <SelectItem value="week">Неделя</SelectItem>
                <SelectItem value="month">Месяц</SelectItem>
              </SelectContent>
            </Select>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={handlePrevPeriod}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToday}
                className="min-w-[100px]"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Сегодня
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextPeriod}
                disabled={isNextDisabled}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Current Period Display */}
          {data && (
            <div className="mt-2 text-center text-sm text-muted-foreground">
              {formatPeriodRange(data.startDate, data.endDate)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-2" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mb-4">
              {error?.message || 'Не удалось загрузить данные'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Повторить
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Total Revenue */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">Выручка</span>
                </div>
                <p className="mt-1 text-xl font-semibold">
                  {formatCurrency(amounts.totalRevenue)}
                </p>
              </CardContent>
            </Card>

            {/* Commission */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-muted-foreground">Комиссия</span>
                </div>
                <p className="mt-1 text-xl font-semibold">
                  {formatCurrency(amounts.totalCommission)}
                </p>
              </CardContent>
            </Card>

            {/* Card Payments */}
            <Card className="col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Картой (Безнал)</span>
                </div>
                <p className="mt-1 text-xl font-semibold">
                  {formatCurrency(amounts.cardAmount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {amounts.cardCount} заказов
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="h-5 w-5 text-muted-foreground" />
                Детализация за {getPeriodLabel(period).toLowerCase()}
              </CardTitle>
              <CardDescription>
                {data && formatPeriodRange(data.startDate, data.endDate)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Completed Orders */}
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span>Завершённые заказы</span>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(amounts.completedAmount)}</p>
                  <p className="text-xs text-muted-foreground">{amounts.completedCount} шт.</p>
                </div>
              </div>

              {/* Card Payments Detail */}
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <span>Оплата картой</span>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(amounts.cardAmount)}</p>
                  <p className="text-xs text-muted-foreground">{amounts.cardCount} шт.</p>
                </div>
              </div>

              {/* Commission Debt */}
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-destructive" />
                  <span>Общий долг комиссий</span>
                </div>
                <div className="text-right">
                  <p className={`font-medium ${amounts.totalCommissionDebt > 0 ? 'text-destructive' : ''}`}>
                    {formatCurrency(amounts.totalCommissionDebt)}
                  </p>
                  <p className="text-xs text-muted-foreground">от всех мастеров</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Экспорт в CSV
            </DialogTitle>
            <DialogDescription>
              Выберите период для экспорта финансовых данных
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Дата начала</label>
                <Input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Дата окончания</label>
                <Input
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                />
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Будут экспортированы все оплаченные и завершённые заказы за выбранный период.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setExportDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleExport}
              disabled={!exportDateFrom || !exportDateTo || exportMutation.isPending}
            >
              {exportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Скачать CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
